/**
 * Scheduler — estado de aplicación de la reconciliación tras una mutación admin.
 *
 * SSOT compartido entre backend (internal-scheduler.js, admin-lotowheel-sorteos.js)
 * y front-admin (lotowheel.js consume el campo en la respuesta del save).
 *
 * Cualquier cambio en estos literales debe sincronizarse en TODOS los consumers —
 * importar la constante en vez de hardcodear strings.
 */

const SCHEDULER_APLICACION = Object.freeze({
    // El scheduler en loto-api fue detenido + iniciado YA con el nuevo intervalo/activo.
    // El admin reportó éxito completo de reconciliación.
    RECARGADO_INMEDIATO: 'recargado_inmediato',
    // Hay apuestas en vuelo (o motivo similar). La reconciliación está agendada para
    // después del próximo ejecutarSorteo + margen. El campo aplicarAlrededorDe en la
    // respuesta indica el timestamp aproximado.
    DIFERIDO: 'diferido',
    // El cambio aplicado no afecta el timer del scheduler (cambio de tema, fichas,
    // etc.) — el scheduler relee config fresca en cada execute(), no necesita
    // recarga.
    SIN_CAMBIOS: 'sin_cambios_en_timer',
});

const SCHEDULER_MOTIVO_DIFERIMIENTO = Object.freeze({
    // Hay apuestas wallet RAM pendientes para el draw actual; recargar ahora
    // resetearía el countdown del cliente mid-apuesta.
    APUESTAS_EN_VUELO: 'apuestas_en_vuelo',
    // Hay bearer tickets multi-sorteo con `ticket_entries` aún no liquidados
    // (estado 'pendiente' o 'activo') para drawSequenceNumbers futuros del
    // sorteo. El cliente compró N jugadas, recibe N jugadas — recargar ahora
    // podría cambiar el intervalo/desactivar el sorteo a mitad de la ventana
    // del ticket. La aplicación se difiere hasta que la última entry se juegue.
    BEARER_TICKETS_MULTI_SORTEO: 'bearer_tickets_multi_sorteo_pendientes',
});

// Códigos de error de negocio en respuestas HTTP. Distinguen "conflicto de
// negocio" de "fallo de transporte" — el front-admin los usa para mostrar UI
// específica (ej: diálogo de confirmación con lista de tickets) en lugar de
// toast genérico de error.
//
// Contrato cross-boundary (backend handler ↔ front-admin Api.callAdminConfirmable):
// el backend devuelve `{ codigo: <valor>, ... }` en HTTP 409, el front
// pregunta `result.codigo === SCHEDULER_CODIGO_ERROR.BEARER_MULTI_SORTEO_PENDIENTES`.
const SCHEDULER_CODIGO_ERROR = Object.freeze({
    // 409 al intentar stop/delete de un sorteo con bearer multi-sorteo
    // `ticket_entries` pendientes/activos. Override con `?confirmar=destruccion`.
    BEARER_MULTI_SORTEO_PENDIENTES: 'BEARER_MULTI_SORTEO_PENDIENTES',
});

// Token de query param que el admin pasa para FORZAR operación destructiva
// (stop / delete) cuando hay bearer multi-sorteo pendientes. El operador asume
// responsabilidad de gestionar reembolso manual.
//
// SSOT cross-boundary: backend lo lee con `request.query?.confirmar ===
// SCHEDULER_CONFIRMAR_DESTRUCCION`, front lo construye en la URL como
// `?confirmar=${SCHEDULER_CONFIRMAR_DESTRUCCION}`.
const SCHEDULER_CONFIRMAR_DESTRUCCION = 'destruccion';

// Múltiplo de `intervalo_segundos` que se aplica como diferimiento safe-default
// cuando la query de bearer multi-sorteo falla (DB locked, disco lleno, etc.).
// 5 intervalos = margen amplio para que el reconciliador background re-evalúe
// con datos frescos. Bloquea operación destructiva sin bloquear indefinidamente.
const INTERVALOS_SAFE_DEFAULT_BEARER_QUERY_FALLIDA = 5;

// Cuando se llega al cap de re-postergación por bearer multi-sorteo, empujamos
// `aplicar_no_antes_de` del outbox este tiempo al futuro para que el background
// reconciler NO retome el caso automáticamente y entremos en loop. El operador
// resuelve manualmente con ?aplicar=ahora o ?confirmar=destruccion.
// 24h = balance entre "no es eterno" y "no se reactiva solo por accidente".
const BEARER_CAP_POSTERGACION_FUTURO_MS = 24 * 60 * 60 * 1000;

// Límites de cuántos códigos de ticket se incluyen en distintos contextos.
// Toast operador: corto, lista visible sin scrolling.
// Confirm dialog front: más amplio para que el operador decida con info.
// Audit log: 20 para forense sin saturar JSON de la fila.
const TICKETS_MAX_TOAST = 5;
const TICKETS_MAX_CONFIRM_DIALOG = 10;
const TICKETS_MAX_AUDIT_LOG = 20;

// Dual export ESM (fronts) + CJS (backend).
if (typeof module !== 'undefined' && module.exports) {
    module.exports = {
        SCHEDULER_APLICACION,
        SCHEDULER_MOTIVO_DIFERIMIENTO,
        SCHEDULER_CODIGO_ERROR,
        SCHEDULER_CONFIRMAR_DESTRUCCION,
        INTERVALOS_SAFE_DEFAULT_BEARER_QUERY_FALLIDA,
        BEARER_CAP_POSTERGACION_FUTURO_MS,
        TICKETS_MAX_TOAST,
        TICKETS_MAX_CONFIRM_DIALOG,
        TICKETS_MAX_AUDIT_LOG,
    };
}

export {
    SCHEDULER_APLICACION,
    SCHEDULER_MOTIVO_DIFERIMIENTO,
    SCHEDULER_CODIGO_ERROR,
    SCHEDULER_CONFIRMAR_DESTRUCCION,
    INTERVALOS_SAFE_DEFAULT_BEARER_QUERY_FALLIDA,
    BEARER_CAP_POSTERGACION_FUTURO_MS,
    TICKETS_MAX_TOAST,
    TICKETS_MAX_CONFIRM_DIALOG,
    TICKETS_MAX_AUDIT_LOG,
};
